import { useEffect, useState } from 'react';
import { ModalPortal } from '@/components/ui/Modal/ModalPortal';
import { Pencil, Loader2, AlertCircle, ToggleLeft, ToggleRight } from 'lucide-react';

export interface EstadoCatalogoAdmin {
  id: number;
  tipo?: 'suscripcion' | 'transaccion' | string;
  layer?: 'subscription' | 'transaction' | string;
  valor?: string;
  value?: string;
  label: string;
  color: string;
  icono?: string;
  icon?: string;
  descripcion?: string;
  description?: string;
  activo?: boolean;
  isActive?: boolean;
}

/** Badges con contraste AA en light y dark */
const COLOR_BADGE: Record<string, string> = {
  green:
    'bg-emerald-100 text-emerald-900 border border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-200 dark:border-emerald-500/30',
  blue:
    'bg-sky-100 text-sky-950 border border-sky-200 dark:bg-sky-500/20 dark:text-sky-200 dark:border-sky-500/30',
  yellow:
    'bg-amber-100 text-amber-950 border border-amber-200 dark:bg-amber-500/20 dark:text-amber-100 dark:border-amber-500/30',
  red:
    'bg-rose-100 text-rose-950 border border-rose-200 dark:bg-rose-500/20 dark:text-rose-200 dark:border-rose-500/30',
  orange:
    'bg-orange-100 text-orange-950 border border-orange-200 dark:bg-orange-500/20 dark:text-orange-100 dark:border-orange-500/30',
  gray:
    'bg-slate-200 text-slate-900 border border-slate-300 dark:bg-slate-500/25 dark:text-slate-100 dark:border-slate-500/40',
};

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  };
}

export default function EstadosHomologadosPage() {
  const [estados, setEstados] = useState<EstadoCatalogoAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EstadoCatalogoAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<Partial<EstadoCatalogoAdmin>>({});

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/super-admin/estados-catalogo', {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Error al cargar catálogo');
      const json = await res.json();
      setEstados(
        (json.data || []).map((e: any) => ({
          ...e,
          tipo:
            e.layer === 'subscription'
              ? 'suscripcion'
              : e.layer === 'transaction'
                ? 'transaccion'
                : e.tipo || e.layer,
          layer: e.layer || e.tipo,
          valor: e.valor ?? e.value,
          value: e.value ?? e.valor,
          icono: e.icono ?? e.icon,
          icon: e.icon ?? e.icono,
          descripcion: e.descripcion ?? e.description,
          activo: e.activo ?? e.isActive,
          isActive: e.isActive ?? e.activo,
        })),
      );
    } catch (e: any) {
      setError(e.message || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEdit = (item: EstadoCatalogoAdmin) => {
    setEditing(item);
    setForm({ ...item });
  };

  const handleSave = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      const res = await fetch(`/api/super-admin/estados-catalogo/${editing.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          label: form.label,
          color: form.color,
          icon: form.icono ?? form.icon,
          description: form.descripcion ?? form.description,
          isActive: form.activo ?? form.isActive,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Error al guardar');
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const suscripcion = estados.filter(
    (e) => e.tipo === 'suscripcion' || e.layer === 'subscription',
  );
  const transaccion = estados.filter(
    (e) => e.tipo === 'transaccion' || e.layer === 'transaction',
  );

  const isActive = (item: EstadoCatalogoAdmin) => item.activo ?? item.isActive ?? true;

  const renderRail = (title: string, items: EstadoCatalogoAdmin[], delayBase: number) => (
    <section className="space-y-3" aria-labelledby={`rail-${title}`}>
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={`rail-${title}`} className="text-lg font-semibold text-lyrax-text tracking-tight">
          {title}
        </h2>
        <span className="badge badge-neutral">{items.length} estados</span>
      </div>
      <ul className="space-y-3">
        {items.map((item, idx) => (
          <li key={item.id}>
            <article
              className="card group flex items-start justify-between gap-3 transition-shadow duration-200 hover:shadow-lyrax"
              style={{
                animation: 'fade-up 0.45s ease both',
                animationDelay: `${delayBase + idx * 50}ms`,
              }}
            >
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-semibold ${
                      COLOR_BADGE[item.color] || COLOR_BADGE.gray
                    }`}
                  >
                    {item.icono && <span aria-hidden>{item.icono}</span>}
                    {item.label}
                  </span>
                  <code className="text-sm font-mono text-lyrax-text bg-lyrax-bg border border-lyrax-border px-2 py-0.5 rounded">
                    {item.valor || item.value}
                  </code>
                  {!isActive(item) && <span className="badge badge-danger">Inactivo</span>}
                </div>
                {(item.descripcion || item.description) && (
                  <p className="meta-text">{item.descripcion || item.description}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => openEdit(item)}
                className="icon-btn"
                aria-label={`Editar estado ${item.label}`}
              >
                <Pencil className="w-4 h-4" aria-hidden />
              </button>
            </article>
          </li>
        ))}
      </ul>
    </section>
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center h-64 meta-text"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="w-8 h-8 animate-spin text-lyrax-primary mr-2" aria-hidden />
        Cargando estados…
      </div>
    );
  }

  return (
    <div>
      <header className="mb-8 space-y-2">
        <p className="label-caps text-lyrax-primary">public_config</p>
        <h1 className="page-title">Estados homologados</h1>
        <p className="page-subtitle max-w-2xl">
          Catálogo SaaS mandatorio para analítica. Valores fijos; edita etiqueta, color e icono.
          Tenants asignan mapeos contra estos estados.
        </p>
      </header>

      {error && (
        <div
          className="mb-6 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200"
          role="alert"
        >
          <AlertCircle className="w-5 h-5 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {renderRail('Suscripción', suscripcion, 0)}
        {renderRail('Transacción', transaccion, 80)}
      </div>

      {editing && (
        <ModalPortal>
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-estado-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setEditing(null);
          }}
        >
          <div className="modal-panel max-w-lg">
            <div className="modal-header">
              <div>
                <p className="label-caps mb-1">{editing.tipo}</p>
                <h3 id="edit-estado-title" className="text-lg font-bold text-lyrax-text tracking-tight">
                  Editar ·{' '}
                  <span className="font-mono text-emerald-800 dark:text-lyrax-primary">
                    {editing.valor}
                  </span>
                </h3>
              </div>
            </div>

            <div className="modal-body">
              <div>
                <label htmlFor="estado-label" className="field-label">
                  Etiqueta
                </label>
                <input
                  id="estado-label"
                  className="field-input"
                  value={form.label || ''}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="estado-color" className="field-label">
                  Color
                </label>
                <select
                  id="estado-color"
                  className="field-input"
                  value={form.color || 'gray'}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                >
                  {Object.keys(COLOR_BADGE).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="estado-icono" className="field-label">
                  Icono
                </label>
                <input
                  id="estado-icono"
                  className="field-input"
                  value={form.icono || ''}
                  onChange={(e) => setForm({ ...form, icono: e.target.value })}
                  maxLength={10}
                />
              </div>

              <div>
                <label htmlFor="estado-desc" className="field-label">
                  Descripción
                </label>
                <textarea
                  id="estado-desc"
                  rows={2}
                  className="field-input resize-none"
                  value={form.descripcion || ''}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                />
              </div>

              <button
                type="button"
                onClick={() =>
                  setForm({
                    ...form,
                    activo: !(form.activo ?? form.isActive),
                    isActive: !(form.activo ?? form.isActive),
                  })
                }
                className="flex items-center gap-2.5 w-full rounded-lyrax border border-lyrax-border bg-lyrax-bg px-3.5 py-3 text-sm font-medium text-lyrax-text hover:border-lyrax-primary/50 transition-colors"
                aria-pressed={!!(form.activo ?? form.isActive)}
              >
                {form.activo ?? form.isActive ? (
                  <ToggleRight className="w-6 h-6 text-emerald-700 dark:text-lyrax-primary shrink-0" aria-hidden />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-lyrax-text-secondary shrink-0" aria-hidden />
                )}
                <span>{form.activo ?? form.isActive ? 'Activo' : 'Inactivo'}</span>
              </button>
            </div>

            <div className="modal-footer">
              <button type="button" onClick={() => setEditing(null)} className="btn-secondary">
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary"
              >
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
